import React, { useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import { CheckCircle, Loader2, ArrowLeft } from "lucide-react";

export default function VimoRegistration1({ onRegistered, onOtpVerified, onBack, initialPhase }) {
  const username = useSelector((s) => s.auth?.employeeDetails?.username);
  const { lat, lng } = useSelector((s) => s.location);
  const [ip, setIp] = useState("");

  const { callApi: registerApi } = useApi("/aeps/vimopay/merchant/register-from-user", "POST");
  const { callApi: sendOtpApi } = useApi("/aeps/vimopay/otp/send-from-user", "POST");
  const { callApi: resendOtpApi } = useApi("/aeps/vimopay/otp/resend-from-user", "POST");
  const { callApi: validateOtpApi } = useApi("/aeps/vimopay/otp/validate-from-user", "POST");
  const { callApi: statesApi } = useApi("/aeps/vimopay/states", "GET");
  const { callApi: districtsApi } = useApi("/aeps/vimopay/district", "POST");

  const [phase, setPhase] = useState(initialPhase || "register"); // "register" | "otp"
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState("");

  React.useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((r) => r.json())
      .then((d) => setIp(d.ip || ""))
      .catch(() => setIp(""));
  }, []);

  React.useEffect(() => {
    if (initialPhase === "otp") return;
    setStatesLoading(true);
    statesApi(null)
      .then((res) => setStates(Array.isArray(res) ? res : []))
      .catch(() => setStates([]))
      .finally(() => setStatesLoading(false));
  }, []);

  React.useEffect(() => {
    if (!selectedState) { setDistricts([]); setSelectedDistrict(""); return; }
    setDistrictsLoading(true);
    setSelectedDistrict("");
    districtsApi({ stateCode: selectedState })
      .then((res) => setDistricts(Array.isArray(res) ? res : []))
      .catch(() => setDistricts([]))
      .finally(() => setDistrictsLoading(false));
  }, [selectedState]);

  React.useEffect(() => {
    if (initialPhase !== "otp") return;
    setLoading(true);
    setMessage("Sending OTP to your registered mobile...");
    sendOtpApi({ mid: username })
      .then((res) => {
        if (res?.status === "000") {
          setMessage("OTP sent to your registered mobile number.");
          setIsError(false);
        } else {
          setIsError(true);
          setMessage(res?.statusDescription || "Failed to send OTP. Please retry.");
        }
      })
      .catch(() => {
        setIsError(true);
        setMessage("Something went wrong sending OTP.");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleRegister = async () => {
    if (mobile.length !== 10) {
      setIsError(true);
      setMessage("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!selectedState) {
      setIsError(true);
      setMessage("Please select a state.");
      return;
    }
    if (!selectedDistrict) {
      setIsError(true);
      setMessage("Please select a district.");
      return;
    }
    if (!fullName.trim()) {
      setIsError(true);
      setMessage("Please enter your full name.");
      return;
    }
    try {
      setLoading(true);
      setMessage("");
      setIsError(false);

      const payload = {
        mid: username,
        mobile,
        latitude: lat?.toString() || "0",
        longitude: lng?.toString() || "0",
        ipAddress: ip,
        pipe: "4",
        merchantStateCode: selectedState,
        shopStateCode: selectedState,
        merchantDistrictCode: selectedDistrict,
        shopDistrictCode: selectedDistrict,
        fullName: fullName.trim(),
      };

      const res = await registerApi(payload);

      if (res?.status === "000") {
        setMessage(res.statusDescription || "Registration successful. Sending OTP...");
        const otpRes = await sendOtpApi({ mid: username });
        if (otpRes?.status === "000") {
          setMessage("OTP sent to your registered mobile number.");
          setPhase("otp");
          if (onRegistered) onRegistered();
        } else {
          setIsError(true);
          setMessage(otpRes?.statusDescription || "Failed to send OTP. Please retry.");
        }
      } else {
        setIsError(true);
        setMessage(res?.statusDescription || "Registration failed. Please try again.");
      }
    } catch {
      setIsError(true);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setLoading(true);
      setMessage("");
      const res = await resendOtpApi({ mid: username });
      if (res?.status === "000") {
        setMessage("OTP resent to your registered mobile number.");
        setIsError(false);
      } else {
        setIsError(true);
        setMessage(res?.statusDescription || "Failed to resend OTP.");
      }
    } catch {
      setIsError(true);
      setMessage("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleValidateOtp = async () => {
    if (otp.length < 4) return;
    try {
      setLoading(true);
      setMessage("");
      setIsError(false);

      const res = await validateOtpApi({ mid: username, otp });

      if (res?.status === "000") {
        setMessage("OTP verified successfully!");
        if (onOtpVerified) onOtpVerified();
      } else {
        setIsError(true);
        setMessage(res?.statusDescription || "Invalid OTP. Please try again.");
      }
    } catch {
      setIsError(true);
      setMessage("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (phase === "otp") {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto mt-6">
        <button
          onClick={() => setPhase("register")}
          className="flex items-center gap-2 mb-6 text-gray-600 hover:text-blue-600"
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Verify OTP</h2>
            <p className="text-sm text-slate-500">OTP sent to your registered mobile</p>
          </div>
        </div>

        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          placeholder="Enter OTP"
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-4 text-2xl tracking-[0.4em] text-center font-mono focus:outline-none focus:border-blue-500 mb-4"
        />

        <button
          onClick={handleValidateOtp}
          disabled={loading || otp.length < 4}
          className="w-full bg-[#12345A] hover:bg-[#0F2E47] text-white py-4 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? "Verifying..." : "Verify OTP"}
        </button>

        <button
          onClick={handleResendOtp}
          disabled={loading}
          className="w-full border-2 border-slate-200 text-slate-600 py-3 rounded-xl font-medium hover:bg-slate-50 disabled:opacity-50"
        >
          Resend OTP
        </button>

        {message && (
          <div className={`mt-4 p-4 rounded-xl text-sm text-center font-medium ${isError ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
            {message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 w-full mt-6">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-2 mb-6 text-gray-600 hover:text-blue-600"
        >
          <ArrowLeft size={18} />
          Back to AEPS
        </button>
      )}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Merchant Registration</h2>
          <p className="text-sm text-slate-500">Register your outlet for AEPS</p>
        </div>
      </div>

      <div className="bg-slate-50 rounded-xl p-4 mb-6 text-sm text-slate-600 space-y-1">
        <p><span className="font-medium">Merchant ID:</span> {username}</p>
        <p><span className="font-medium">Location:</span> {lat ? `${lat}, ${lng}` : "Fetching..."}</p>
      </div>

      <div className="mb-5">
        <p className="text-red-600 font-bold text-sm mb-1.5">Name should match with PAN Card</p>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="Enter full name as on PAN Card"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            if (message) { setMessage(""); setIsError(false); }
          }}
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition"
        />
      </div>

      <div className="mb-5">
        <p className="text-red-600 font-bold text-sm mb-1.5">Please enter the mobile number linked with your Aadhaar.</p>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Mobile Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          placeholder="Enter 10-digit mobile number"
          value={mobile}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "");
            setMobile(v);
            if (message) { setMessage(""); setIsError(false); }
          }}
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base font-semibold tracking-wider focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition"
        />
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          State <span className="text-red-500">*</span>
        </label>
        <select
          value={selectedState}
          onChange={(e) => setSelectedState(e.target.value)}
          disabled={statesLoading}
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition bg-white disabled:opacity-60"
        >
          <option value="">{statesLoading ? "Loading states..." : "Select state"}</option>
          {states.map((s) => (
            <option key={s.code} value={s.code}>{s.description}</option>
          ))}
        </select>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          District <span className="text-red-500">*</span>
        </label>
        <select
          value={selectedDistrict}
          onChange={(e) => setSelectedDistrict(e.target.value)}
          disabled={!selectedState || districtsLoading}
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition bg-white disabled:opacity-60"
        >
          <option value="">
            {!selectedState ? "Select a state first" : districtsLoading ? "Loading districts..." : "Select district"}
          </option>
          {districts.map((d) => (
            <option key={d.code} value={d.code}>{d.description}</option>
          ))}
        </select>
      </div>

      <button
        onClick={handleRegister}
        disabled={loading || !fullName.trim() || mobile.length !== 10 || !selectedState || !selectedDistrict}
        className="w-full bg-[#12345A] hover:bg-[#0F2E47] text-white py-4 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {loading ? "Registering..." : "Register & Get OTP"}
      </button>

      {message && (
        <div className={`mt-4 p-4 rounded-xl text-sm text-center font-medium ${isError ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
          {message}
        </div>
      )}
    </div>
  );
}
